import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {view} from '@sail/ui';
import {useWidgetControls} from '@stripe-internal/conversations-ui';
import {Conversation} from '@stripe-internal/conversations-client';
import {useClientAnalytics} from '../../Providers/ClientProvider/useClientAnalytics';
import {RatingValue} from '../../components/Forms/CSAT/types';
import {useApi} from '../../Providers/ApiProvider/ApiProvider';
import {ScreenName, useScreen} from '../../Providers/ScreenProvider';
import {DotComSurveyForm} from '../../components/Forms/DotComCSAT/DotComSurveyForm';
import {useMetrics} from '../../Providers/ObservabilityProvider';

export type Props = {
  conversation: Conversation;
};

const messages = defineMessages({
  genericError: {
    id: 'support_conversations_client.csat.dotcom.generic_error',
    description:
      'Generic error message for when user is not able to submit a CSAT rating',
    defaultMessage: 'An error occurred. Please try again later.',
  },
});

export function DotComCSATScreen({conversation}: Props) {
  const intl = useIntl();
  const analytics = useClientAnalytics();
  const {close} = useWidgetControls();
  const metrics = useMetrics();
  const {screen} = useScreen();
  const {submitFeedback} = useApi();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const sentImpression = useRef<boolean>(false);

  const analyticsParams = useMemo(
    () => ({
      universalHelpConversationId: conversation.id,
      audience: conversation.context.audience,
      userId: conversation.user.id,
    }),
    [conversation],
  );

  useEffect(() => {
    if (!sentImpression.current) {
      analytics.action(
        'support_conversations_dotcom_csat_survey_impression',
        analyticsParams,
      );
      metrics.increment('dot_com_conversations_client.csat.viewed');
      sentImpression.current = true;
    }
  }, [analytics, analyticsParams, metrics]);

  const handleClose = useCallback(() => {
    if (conversation.context.intent === true) {
      conversation.setContextValue('dotComCSAT', false);
    }
    close(true);

    if (conversation.isActive) {
      conversation.leave();
    } else {
      conversation.disconnect();
      conversation.clearStorage();
    }
  }, [conversation, close]);

  useEffect(() => {
    const sub = conversation.contextObservable().subscribe(() => {
      if (
        screen.name === ScreenName.DotComCSAT &&
        !conversation.context.dotComCSAT
      ) {
        handleClose();
      }
    });

    return () => sub.unsubscribe();
  }, [conversation, handleClose, screen]);

  const handleSubmit = useCallback(
    async ({rating, comment}: {rating: RatingValue; comment: string}) => {
      setIsLoading(true);
      analytics.action('support_conversations_dotcom_csat_survey_submit', {
        ...analyticsParams,
        has_additional_feedback: !!comment,
        rating,
      });
      try {
        await submitFeedback({
          feedbackScore: parseInt(rating, 10),
          comment,
          conversationId: conversation.id,
        });
        setIsLoading(false);
        analytics.action(
          'support_conversations_dotcom_csat_survey_submit_success',
          analyticsParams,
        );

        handleClose();
      } catch (e) {
        setErrorMessage(intl.formatMessage(messages.genericError));
        setIsLoading(false);
        analytics.action(
          'support_conversations_dotcom_csat_survey_submit_error',
          analyticsParams,
        );
      }
    },
    [
      intl,
      analytics,
      handleClose,
      analyticsParams,
      conversation.id,
      submitFeedback,
    ],
  );

  return (
    <view.div
      css={{
        padding: 'medium',
        background: 'white',
        maxHeight: 'fill',
        height: '100%',
      }}
    >
      <DotComSurveyForm
        error={errorMessage}
        loading={isLoading}
        onSubmit={handleSubmit}
        onDismiss={handleClose}
        analyticsParams={analyticsParams}
      />
    </view.div>
  );
}
